import { hierarchy, partition, HierarchyRectangularNode } from "d3-hierarchy";
import { select } from "d3-selection";
import { scaleOrdinal } from "d3-scale";
import { arc as d3Arc } from "d3-shape";
import { Category } from "../../../databinding/Categorize";
import { Panel } from "../../panel/Panel";
import { Label } from "../../label/Label";

export interface CategorySunburstOptions {
    width?: number;
    height?: number;
}

export class CategorySunburst {
    private defaultOptions: CategorySunburstOptions = {
        width: 850,
        height: 850
    };

    constructor(private options: CategorySunburstOptions = {}) {
        this.options = { ...this.defaultOptions, ...options };
    }

    render(
        container: HTMLElement,
        categories: Category[],
        rootCategories: Category[],
        containerPanel: Panel,
        categoryFieldLabel: Label | undefined,
        formatTooltip: (cat: Category) => string,
        convertToHierarchy: (cats: Category[]) => any,
        updateFieldLabel: (cats: Category[]) => string
    ): void {
        const { width, height } = this.options;
        const radius = Math.min(width, height) / 2;

        // Update the category field label
        if (categoryFieldLabel) {
            categoryFieldLabel.text = updateFieldLabel(categories);
        }

        // Convert categories to D3 hierarchy format
        const hierarchyData = convertToHierarchy(categories);

        // Create D3 hierarchy
        const root = hierarchy<any>(hierarchyData)
            .sum((d: any) => d.value || 0)
            .sort((a, b) => (b.value || 0) - (a.value || 0));

        // Create partition layout for sunburst
        const partitionLayout = partition<any>()
            .size([2 * Math.PI, radius]);

        const partitionRoot = partitionLayout(root) as HierarchyRectangularNode<any>;

        // Create SVG
        const svg = select(container)
            .append("svg")
            .attr("viewBox", `${-width / 2} ${-height / 2} ${width} ${height}`)
            .attr("width", "100%")
            .attr("height", "100%")
            .style("font", "12px sans-serif")
            .style("background", "#fafafa");

        // Create color scale
        const color = scaleOrdinal<string>()
            .domain(["0", "1", "2", "3", "4", "5"])
            .range(["#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd", "#8c564b"]);

        const g = svg.append("g");

        // Create arc generator
        const arcGenerator = d3Arc<HierarchyRectangularNode<any>>()
            .startAngle((d: any) => d.x0)
            .endAngle((d: any) => d.x1)
            .padAngle((d: any) => Math.min((d.x1 - d.x0) / 2, 0.005))
            .padRadius(radius / 2)
            .innerRadius((d: any) => d.y0)
            .outerRadius((d: any) => d.y1 - 1);

        // Create nodes
        const path = g.selectAll<SVGPathElement, HierarchyRectangularNode<any>>("path")
            .data(partitionRoot.descendants().filter((d: any) => d.depth > 0))
            .join("path")
            .attr("d", arcGenerator)
            .attr("fill", (d: HierarchyRectangularNode<any>) => {
                return color(d.depth.toString());
            })
            .attr("stroke", "#fff")
            .attr("stroke-width", 2)
            .style("cursor", "pointer")
            .style("fill-opacity", 0.8)
            .on("mouseenter", function(event: MouseEvent, d: HierarchyRectangularNode<any>) {
                select(this)
                    .style("fill-opacity", 1)
                    .attr("stroke-width", 3);
            })
            .on("mouseleave", function(event: MouseEvent, d: HierarchyRectangularNode<any>) {
                select(this)
                    .style("fill-opacity", 0.8)
                    .attr("stroke-width", 2);
            });

        // Add tooltips
        path.append("title")
            .text((d: HierarchyRectangularNode<any>) => {
                let tooltip = "";

                // Use the original category if available
                if (d.data.originalCategory) {
                    tooltip = formatTooltip(d.data.originalCategory);
                } else {
                    // Build from hierarchy
                    const pathParts: string[] = [];
                    let current: HierarchyRectangularNode<any> | null = d;
                    while (current && current.depth > 0) {
                        if (current.data.fieldName && current.data.fieldValue) {
                            pathParts.unshift(`${current.data.fieldName}:${current.data.fieldValue}`);
                        } else {
                            pathParts.unshift(current.data.name);
                        }
                        current = current.parent as HierarchyRectangularNode<any> | null;
                    }
                    tooltip = pathParts.join(" > ") + "\n";
                }

                tooltip += `Items: ${d.data.itemCount}`;
                return tooltip;
            });

        // Add text labels for arcs that are large enough
        const text = g.selectAll<SVGTextElement, HierarchyRectangularNode<any>>("text")
            .data(partitionRoot.descendants().filter((d: any) => d.depth > 0 && (d.y1 - d.y0) > 20))
            .join("text")
            .attr("transform", (d: any) => {
                const x = (((d.x0 + d.x1) / 2) * 180 / Math.PI);
                const y = (d.y0 + d.y1) / 2;
                return `rotate(${x - 90}) translate(${y},0) rotate(${x < 180 ? 0 : 180})`;
            })
            .attr("dy", "0.35em")
            .style("text-anchor", "middle")
            .style("fill", "#fff")
            .style("font-weight", "bold")
            .style("pointer-events", "none")
            .style("user-select", "none")
            .style("font-family", "Arial, sans-serif")
            .style("font-size", "10px")
            .style("text-shadow", "0 1px 2px rgba(0,0,0,0.6)")
            .text((d: HierarchyRectangularNode<any>) => {
                const angleSize = d.x1 - d.x0;
                const radiusSize = d.y1 - d.y0;
                // Only show text if the arc is large enough
                if (angleSize < 0.1 || radiusSize < 25) return "";
                const name = d.data.name || "";
                const arcLength = angleSize * ((d.y0 + d.y1) / 2);
                const maxLength = Math.floor(arcLength / 7);
                const displayText = name.length > maxLength ? name.substring(0, maxLength - 3) + "..." : name;
                return displayText;
            });

        // Add click to drill down functionality
        path.on("click", (event: MouseEvent, d: HierarchyRectangularNode<any>) => {
            event.stopPropagation();
            // Find the category with subcategories
            const clickedCategory = d.data.originalCategory;
            if (clickedCategory && clickedCategory.subcategories && clickedCategory.subcategories.length > 0) {
                container.innerHTML = "";
                this.render(container, clickedCategory.subcategories, rootCategories, containerPanel, categoryFieldLabel, formatTooltip, convertToHierarchy, updateFieldLabel);
            }
        });

        // Click on SVG background to reset to original categories
        svg.on("click", () => {
            container.innerHTML = "";
            this.render(container, rootCategories, rootCategories, containerPanel, categoryFieldLabel, formatTooltip, convertToHierarchy, updateFieldLabel);
        });

        // Add center circle with reset text
        const centerGroup = g.append("g")
            .style("cursor", "pointer")
            .on("click", () => {
                container.innerHTML = "";
                this.render(container, rootCategories, rootCategories, containerPanel, categoryFieldLabel, formatTooltip, convertToHierarchy, updateFieldLabel);
            });

        centerGroup.append("circle")
            .attr("r", radius / 6)
            .attr("fill", "#fff")
            .attr("stroke", "#999")
            .attr("stroke-width", 2);

        centerGroup.append("text")
            .attr("text-anchor", "middle")
            .attr("dy", "0.35em")
            .style("font-size", "14px")
            .style("font-weight", "bold")
            .style("fill", "#333")
            .style("pointer-events", "none")
            .text("Reset");
    }
}
